import React from 'react';
import { Box, Button, Stack } from '@mui/material';

import { PropertyTextInput } from '@paragon/client-tools';
import { notNullish } from '@paragon/utilities';

import { ReferenceLineIntersectionGuidKeys } from '../../../../library/relativeGeometry/getReferenceLineEditableIntersectionPoints';
import { getPickingObjectInfo } from '../../../../store/selectors/pickingSelectors';
import {
  clearHighlightedReferenceLineGuids,
  setHighlightedReferenceLineGuids,
} from '../../../../store/slices/viewportSlice';
import { useAppDispatch, useAppSelector } from '../../../hooks';

interface Props<T extends { readonly [K0 in K]: string | null }, K extends keyof T> {
  referenceLineLongNamesByGuid: ReadonlyMap<string, string>;
  line: T;
  disabled?: boolean;
  lineKeys: ReferenceLineIntersectionGuidKeys<K>;
  property: string;
  onStartSelectingIntersection: (line: T, key1: keyof T, key2: keyof T) => void;
}

export default <T extends { readonly [K0 in K]: string | null }, K extends keyof T>(props: Props<T, K>) => {
  const dispatch = useAppDispatch();

  const pickingObjectInfo = useAppSelector(getPickingObjectInfo);
  const isPickingReferenceLineOrIntersection =
    (pickingObjectInfo?.type === 'pickingReferenceLine' && pickingObjectInfo.targetInfo.type === 'referenceLine') ||
    pickingObjectInfo?.type === 'pickingReferenceLineIntersection';

  const { line, lineKeys, referenceLineLongNamesByGuid } = props;
  const referencedLine1Guid = line[lineKeys.key1];
  const referencedLine2Guid = line[lineKeys.key2];

  const firstLineName = (referencedLine1Guid && referenceLineLongNamesByGuid.get(referencedLine1Guid)) ?? 'unknown';
  const secondLineName = (referencedLine2Guid && referenceLineLongNamesByGuid.get(referencedLine2Guid)) ?? 'unknown';

  return (
    <Stack
      direction="row"
      spacing={1}
      onMouseEnter={() => {
        dispatch(
          setHighlightedReferenceLineGuids([referencedLine1Guid, referencedLine2Guid].filter(notNullish) as string[]),
        );
      }}
      onMouseLeave={() => {
        dispatch(clearHighlightedReferenceLineGuids());
      }}
    >
      <PropertyTextInput disabled fullWidth property={props.property} value={`${firstLineName} ✕ ${secondLineName}`} />

      <Box sx={{ display: 'flex' }}>
        <Button
          variant="outlined"
          disabled={props.disabled || isPickingReferenceLineOrIntersection}
          onClick={() => {
            props.onStartSelectingIntersection(props.line, lineKeys.key1, lineKeys.key2);
          }}
        >
          Pick
        </Button>
      </Box>
    </Stack>
  );
};
